import React, {
  createContext,
  useContext,
  useEffect,
  useRef,
  useState,
  useMemo,
} from "react";
import PropTypes from "prop-types";
import toast from '../../i18n/toast';
import service from "../../services/SupportReportService";
import SupportReportComposer from "./SupportReportComposer";
import { prepareSource } from "./sourceCapture";
import version from "../../version";
import { useTranslation } from 'react-i18next';

const SupportContext = createContext(null);
export const useSupport = () => useContext(SupportContext);
function ScopedSupport({ user, children }) {
  const { t: tr } = useTranslation();
  const [capabilities, setCapabilities] = useState(null);
  const [capabilityError, setCapabilityError] = useState("");
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState({
    type: "BUG",
    description: "",
    image: null,
    source: null,
  });
  const [generation, setGeneration] = useState(0);
  const source = useRef(null);
  const alive = useRef(true);
  const revision = useRef(0);
  const base = user.role === "CLIENT" ? "/client/support" : "/manage/support";
  const refresh = async () => {
    const current = ++revision.current;
    try {
      const value = await service.getCapabilities();
      if (alive.current && current === revision.current) {
        setCapabilities(value);
        setCapabilityError("");
      }
    } catch (error) {
      if (alive.current && current === revision.current) {
        setCapabilities(null);
        setCapabilityError(error.message);
      }
    }
  };
  useEffect(() => {
    refresh();
    const visible = () => {
      if (document.visibilityState === "visible") refresh();
    };
    document.addEventListener("visibilitychange", visible);
    window.addEventListener("support-setting-changed", refresh);
    return () => {
      alive.current = false;
      if (source.current) source.current.dispose();
      document.removeEventListener("visibilitychange", visible);
      window.removeEventListener("support-setting-changed", refresh);
    };
  }, []);
  const launch = () => {
    if (!draft.source) {
      try {
        source.current = prepareSource(document.body, {
          width: window.innerWidth,
          height: window.innerHeight,
          scrollX: window.scrollX,
          scrollY: window.scrollY,
        });
      } catch (error) {
        source.current = null;
      }
      setDraft((current) => ({
        ...current,
        source: {
          pathname: window.location.pathname,
          viewLabel: document.title,
          viewKey: window.location.pathname,
          appVersion: version,
        },
      }));
    }
    setOpen(true);
  };
  const success = (result) => {
    if (!alive.current) return;
    setOpen(false);
    setDraft({ type: "BUG", description: "", image: null, source: null });
    if (source.current) source.current.dispose();
    source.current = null;
    setGeneration((value) => value + 1);
    const number = result.report.reportNumber;
    toast.success(number ? { key: 'manage:support.sent', params: { number } } : tr("manage:integratedText30"));
  };
  const contextValue = useMemo(
    () => ({ capabilities, capabilityError, refresh, generation, user, base, launch }),
    [capabilities, capabilityError, refresh, generation, user, base, launch],
  );
  return (
    <SupportContext.Provider value={contextValue}>
      {children}
      {capabilities && capabilities.enabled && (
        <button
          data-support-ui="true"
          type="button"
          className="btn manage-red-button support-launcher"
          onClick={launch}
        >{tr("manage:integratedText31")}</button>
      )}
      <SupportReportComposer
        open={open}
        onClose={() => setOpen(false)}
        draft={draft}
        setDraft={setDraft}
        snapshot={source.current}
        onSuccess={success}
        enabled={Boolean(capabilities && capabilities.enabled)}
      />
    </SupportContext.Provider>
  );
}
ScopedSupport.propTypes = {
  user: PropTypes.object.isRequired,
  children: PropTypes.node.isRequired,
};
export default function SupportReportsProvider({ user, children }) {
  return (
    <ScopedSupport
      key={JSON.stringify([
        user.id,
        user.schoolId,
        user.role,
        user.platformRoles,
      ])}
      user={user}
    >
      {children}
    </ScopedSupport>
  );
}
SupportReportsProvider.propTypes = ScopedSupport.propTypes;
